import { createAsyncThunk } from '@reduxjs/toolkit'
import { isCommonResponseSuccessful, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  selectAppointmentCalendarSettings,
  setAppointmentCalendarSettingsData,
  setAppointmentCalendarSettingsError,
  setAppointmentCalendarSettingsFetchedFor,
  setAppointmentCalendarSettingsStatus,
} from '../prescriptionReducer'
import { REDUX_STATUS_FAILED, REDUX_STATUS_LOADING, REDUX_STATUS_SUCCESS } from 'src/consumer/constants'

// Thunk
export const getAppointmentCalendarSettings = createAsyncThunk(
  'prescriptionStore/appointmentCalendarSettings/get',
  async ({ appointmentCalendarSettingsGetApiSignature }, { getState, dispatch }) => {
    const cached = selectAppointmentCalendarSettings(getState(), false)
    const existingData = cached?.data

    // Cache hit only when the data was fetched with this same API signature —
    // the support portal loads other practitioners' settings into this slice,
    // so data cached for one signature is stale for any other
    if (existingData && cached?.fetchedFor === appointmentCalendarSettingsGetApiSignature) {
      return existingData
    }

    dispatch(setAppointmentCalendarSettingsStatus(REDUX_STATUS_LOADING))
    if (existingData) {
      // cached data belongs to a different signature (e.g. another practitioner
      // viewed via the support portal) — drop it so a failed refetch can't show it
      dispatch(setAppointmentCalendarSettingsData(null))
      dispatch(setAppointmentCalendarSettingsFetchedFor(null))
    }

    try {
      // Check if this is a support portal API
      const isSupportPortalApi = appointmentCalendarSettingsGetApiSignature.includes('/api/secure/cc/support/')
      
      let resp
      if (isSupportPortalApi) {
        // Parse query params from URL if present
        const urlParts = appointmentCalendarSettingsGetApiSignature.split('?')
        const pathname = urlParts[0]
        const params = {}
        
        // Extract query params if present
        if (urlParts.length > 1) {
          const queryString = urlParts[1]
          const searchParams = new URLSearchParams(queryString)
          searchParams.forEach((value, key) => {
            params[key] = value
          })
        }
        
        resp = await getSPDataWithParams(pathname, params)
        
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = resp?.data?.data || resp?.data || {}
          const appointmentCalendarSettings = data?.appointmentCalendarSettings || data
          dispatch(setAppointmentCalendarSettingsData(appointmentCalendarSettings))
          dispatch(setAppointmentCalendarSettingsFetchedFor(appointmentCalendarSettingsGetApiSignature))
          dispatch(setAppointmentCalendarSettingsStatus(REDUX_STATUS_SUCCESS))
        } else {
          throw new Error('Invalid response from API')
        }
      } else {
        // Mentor portal API
        resp = await getMPDataWithoutParams(
          appointmentCalendarSettingsGetApiSignature,
        )
        if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
          // Update store silently with new data
          dispatch(setAppointmentCalendarSettingsData(resp?.data?.data?.appointmentCalendarSettings))
          dispatch(setAppointmentCalendarSettingsFetchedFor(appointmentCalendarSettingsGetApiSignature))
          dispatch(setAppointmentCalendarSettingsStatus(REDUX_STATUS_SUCCESS))
        } else {
          throw new Error('Invalid response from API')
        }
      }
    } catch (error) {
      dispatch(setAppointmentCalendarSettingsError(error.message || 'Failed to fetch data'))
      dispatch(setAppointmentCalendarSettingsStatus(REDUX_STATUS_FAILED))
    }
  },
)
